import { useEffect, useState } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import {
  Container,
  Row,
  Col,
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  Table,
  Badge,
  Progress,
  Button,
  Input,
  InputGroup,
  InputGroupAddon,
  InputGroupText,
  Modal,
  ModalHeader,
  ModalBody,
  Spinner
} from 'reactstrap';
import axios from 'axios';
import Swal from 'sweetalert2';
import {
  fetchAdminAnalytics,
  fetchStudentDetails,
} from '../../../redux/QuestionBank/AdminAnalyticsAction';
import { getSpecialty } from 'config/specialtyConfig';
import { getSpecialtyFields, getPlanById } from 'config/subscriptionPlans';

const DashboardPage = ({ adminAnalytics }) => {
  const dispatch = useDispatch();
  const activeSpecialty = useSelector(state => state.specialty.activeSpecialty);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [studentModalOpen, setStudentModalOpen] = useState(false);
  const [subjectFilter, setSubjectFilter] = useState('all');
  const [activityFilter, setActivityFilter] = useState('all'); // 'all', 'completed', 'in_progress', 'no_activity'


  // Boards exam date
  const [boardsExamDate, setBoardsExamDate] = useState('');
  const [boardsDateSaving, setBoardsDateSaving] = useState(false);

  // Sort state for Student Progress
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('desc');

  // Pagination state for Student Progress
  const [studentPage, setStudentPage] = useState(1);
  const studentPerPage = 10;

  // Sort state for Question Analytics
  const [qSortColumn, setQSortColumn] = useState(null);
  const [qSortDirection, setQSortDirection] = useState('desc');

  // Pagination state for Question Analytics
  const [questionPage, setQuestionPage] = useState(1);
  const questionPerPage = 10;

  const {
    students,
    totalStudents,
    overallStats,
    subjectStats,
    questionAnalytics,
    loading,
    questionLoading,
    studentLoading,
    error
  } = adminAnalytics;

  useEffect(() => {
    if (students.length === 0 && !loading) {
      dispatch(fetchAdminAnalytics()).then(result => {
        if (result.success && result.cacheInfo) {
          setCacheRebuilding(result.cacheInfo.rebuilding || false);
        }
      });
    }
  }, [dispatch, activeSpecialty]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setBoardsExamDate('');
    axios.post('/getBoardsExamDate', { specialty: activeSpecialty }).then(res => {
      if (res.data.success && res.data.date) {
        setBoardsExamDate(res.data.date);
      }
    }).catch(() => {});
  }, [activeSpecialty]);

  const [resettingCache, setResettingCache] = useState(false);
  const [cacheRebuilding, setCacheRebuilding] = useState(false);

  // Poll for cache rebuild completion
  useEffect(() => {
    if (!cacheRebuilding) return;
    const interval = setInterval(async () => {
      const result = await dispatch(fetchAdminAnalytics());
      if (result.success && result.cacheInfo && !result.cacheInfo.rebuilding) {
        setCacheRebuilding(false);
      }
    }, 15000);
    return () => clearInterval(interval);
  }, [cacheRebuilding, dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRefresh = async () => {
    const result = await dispatch(fetchAdminAnalytics());
    if (result.success && result.cacheInfo) {
      setCacheRebuilding(result.cacheInfo.rebuilding || false);
    }
  };

  const handleResetCache = async () => {
    const confirm = await Swal.fire({
      title: 'Reset Analytics Cache?',
      text: 'Only reset if data appears out of sync. This operation uses a lot of server resources.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, reset',
    });
    if (!confirm.isConfirmed) return;

    setResettingCache(true);
    try {
      await axios.post('/resetAnalyticsCache', { specialty: activeSpecialty });
      setCacheRebuilding(true);
      Swal.fire({ icon: 'success', title: 'Cache Rebuild Started', text: 'Cache is rebuilding in the background. Refresh in a moment to see fresh data.', timer: 3000, showConfirmButton: false });
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to reset cache. Please try again.' });
    }
    setResettingCache(false);
  };

  const handleSaveBoardsDate = async () => {
    setBoardsDateSaving(true);
    try {
      await axios.post('/setBoardsExamDate', { date: boardsExamDate, specialty: activeSpecialty });
      Swal.fire({ icon: 'success', title: 'Saved', text: 'Boards exam date updated successfully', timer: 2000, showConfirmButton: false });
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to save boards exam date' });
    }
    setBoardsDateSaving(false);
  };

  const handleClearBoardsDate = async () => {
    setBoardsDateSaving(true);
    try {
      await axios.post('/setBoardsExamDate', { date: null, specialty: activeSpecialty });
      setBoardsExamDate('');
      Swal.fire({ icon: 'success', title: 'Cleared', text: 'Boards exam date has been cleared', timer: 2000, showConfirmButton: false });
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to clear boards exam date' });
    }
    setBoardsDateSaving(false);
  };

  const handleViewStudent = async (userHandle) => {
    const result = await dispatch(fetchStudentDetails(userHandle));
    if (result.success) {
      setSelectedStudent(result.data);
      setStudentModalOpen(true);
    }
  };

  const filteredStudents = students.filter(student => {
    // Search filter
    const matchesSearch = student.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      student.email?.toLowerCase().includes(searchTerm.toLowerCase());

    // Activity filter
    let matchesActivity = true;
    if (activityFilter === 'completed') {
      matchesActivity = student.completedTests > 0;
    } else if (activityFilter === 'in_progress') {
      matchesActivity = student.inProgressTests > 0 && student.completedTests === 0;
    } else if (activityFilter === 'no_activity') {
      matchesActivity = student.totalTests === 0;
    }

    return matchesSearch && matchesActivity;
  });

  const filteredQuestions = questionAnalytics.filter(q =>
    subjectFilter === 'all' || q.subjectId === subjectFilter
  );

  // Sort handler for Student Progress table
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('desc');
    }
  };

  // Compute access priority for sorting: Lifetime=5, UAS=4, Plan=3, Trial=2, Free=1
  const fields = getSpecialtyFields(activeSpecialty);
  const getAccessPriority = (student) => {
    if (student[fields.lifetimeAccess]) return 5;
    if (student[fields.ultimateAdvisingStudent]) return 4;
    if (student[fields.planId]) return 3;
    if (student[fields.trialStartDate]) return 2;
    return 1;
  };

  // Apply sorting to filtered students
  const sortedStudents = [...filteredStudents].sort((a, b) => {
    if (!sortColumn) return 0;

    let aVal, bVal;
    if (sortColumn === 'access') {
      aVal = getAccessPriority(a);
      bVal = getAccessPriority(b);
    } else if (sortColumn === 'pmrProgram') {
      aVal = a[fields.pmrProgram];
      bVal = b[fields.pmrProgram];
    } else if (sortColumn === 'year') {
      aVal = a[fields.year];
      bVal = b[fields.year];
    } else {
      aVal = a[sortColumn];
      bVal = b[sortColumn];
    }

    // String comparison for text columns
    if (sortColumn === 'name' || sortColumn === 'pmrProgram' || sortColumn === 'year' || sortColumn === 'heardAboutUs') {
      const aStr = (aVal || '').toLowerCase();
      const bStr = (bVal || '').toLowerCase();
      if (!aStr && !bStr) return 0;
      if (!aStr) return 1;
      if (!bStr) return -1;
      return sortDirection === 'asc' ? aStr.localeCompare(bStr) : bStr.localeCompare(aStr);
    }

    // Nulls/undefined/zero go to bottom regardless of direction
    const aEmpty = aVal == null || aVal === '' || aVal === 0;
    const bEmpty = bVal == null || bVal === '' || bVal === 0;
    if (aEmpty && bEmpty) return 0;
    if (aEmpty) return 1;
    if (bEmpty) return -1;

    // Date comparison
    if (sortColumn === 'lastActivityDate') {
      aVal = new Date(aVal).getTime();
      bVal = new Date(bVal).getTime();
    }

    if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  // Reset page when filters or sort change
  useEffect(() => {
    setStudentPage(1);
  }, [searchTerm, activityFilter, sortColumn, sortDirection]);

  // Sort handler for Question Analytics table
  const handleQuestionSort = (column) => {
    if (qSortColumn === column) {
      setQSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setQSortColumn(column);
      setQSortDirection('desc');
    }
  };

  // Apply sorting to filtered questions
  const sortedQuestions = [...filteredQuestions].sort((a, b) => {
    if (!qSortColumn) return 0;

    let aVal, bVal;
    // Handle answer distribution columns (A=0, B=1, C=2, D=3)
    if (qSortColumn.startsWith('dist_')) {
      const idx = parseInt(qSortColumn.split('_')[1]);
      aVal = a.answerDistribution?.[idx]?.percentage ?? null;
      bVal = b.answerDistribution?.[idx]?.percentage ?? null;
    } else {
      aVal = a[qSortColumn];
      bVal = b[qSortColumn];
    }

    // String comparison for text columns
    if (['questionCode', 'subjectName', 'unitName', 'topicName'].includes(qSortColumn)) {
      const aStr = (aVal || '').toLowerCase();
      const bStr = (bVal || '').toLowerCase();
      if (!aStr && !bStr) return 0;
      if (!aStr) return 1;
      if (!bStr) return -1;
      return qSortDirection === 'asc' ? aStr.localeCompare(bStr) : bStr.localeCompare(aStr);
    }

    const aEmpty = aVal == null || aVal === '';
    const bEmpty = bVal == null || bVal === '';
    if (aEmpty && bEmpty) return 0;
    if (aEmpty) return 1;
    if (bEmpty) return -1;

    if (aVal < bVal) return qSortDirection === 'asc' ? -1 : 1;
    if (aVal > bVal) return qSortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  useEffect(() => {
    setQuestionPage(1);
  }, [subjectFilter, qSortColumn, qSortDirection]);

  // Pagination calculations for students
  const studentTotalPages = Math.ceil(sortedStudents.length / studentPerPage);
  const studentStartIndex = (studentPage - 1) * studentPerPage;
  const paginatedStudents = sortedStudents.slice(studentStartIndex, studentStartIndex + studentPerPage);

  // Pagination calculations for questions
  const questionTotalPages = Math.ceil(sortedQuestions.length / questionPerPage);
  const questionStartIndex = (questionPage - 1) * questionPerPage;
  const paginatedQuestions = sortedQuestions.slice(questionStartIndex, questionStartIndex + questionPerPage);

  const formatTime = (seconds) => {
    if (!seconds) return '0s';
    if (seconds < 60) return `${seconds}s`;
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'success';
    if (score >= 60) return 'warning';
    return 'danger';
  };

  const getProgressColor = (percent) => {
    if (percent >= 75) return 'success';
    if (percent >= 50) return 'info';
    if (percent >= 25) return 'warning';
    return 'danger';
  };

  // Render sort icon for a column header
  const renderSortIcon = (column) => {
    if (sortColumn !== column) return <i className="fas fa-sort ml-1 text-muted" style={{ fontSize: '0.7rem', opacity: 0.4 }}></i>;
    return sortDirection === 'asc'
      ? <i className="fas fa-sort-up ml-1 text-primary" style={{ fontSize: '0.7rem' }}></i>
      : <i className="fas fa-sort-down ml-1 text-primary" style={{ fontSize: '0.7rem' }}></i>;
  };

  // Render sort icon for question analytics table
  const renderQSortIcon = (column) => {
    if (qSortColumn !== column) return <i className="fas fa-sort ml-1 text-muted" style={{ fontSize: '0.7rem', opacity: 0.4 }}></i>;
    return qSortDirection === 'asc'
      ? <i className="fas fa-sort-up ml-1 text-primary" style={{ fontSize: '0.7rem' }}></i>
      : <i className="fas fa-sort-down ml-1 text-primary" style={{ fontSize: '0.7rem' }}></i>;
  };

  // Get unique subjects for filter
  const uniqueSubjects = [...new Set(questionAnalytics.map(q => q.subjectId))].map(id => ({
    id,
    name: questionAnalytics.find(q => q.subjectId === id)?.subjectName || 'Unknown'
  }));

  return (
    <>
      {/* Header */}
      <div className="header bg-gradient-primary pb-8 pt-5 pt-md-8">
        <Container fluid>
          <div className="header-body">
            <Row className="align-items-center">
              <Col>
                <h1 className="text-white mb-0">Question Bank Analytics</h1>
                <p className="text-white mt-0 mb-0">Monitor student progress and question performance</p>
              </Col>
              <Col className="text-right">
                <Button
                  color="warning"
                  size="sm"
                  className="mr-2"
                  onClick={handleResetCache}
                  disabled={loading || resettingCache}
                >
                  <i className={`fas ${resettingCache ? 'fa-spinner fa-spin' : 'fa-database'} mr-2`}></i>
                  Reset Cache
                </Button>
                <Button
                  color="secondary"
                  size="sm"
                  onClick={handleRefresh}
                  disabled={loading}
                >
                  <i className={`fas ${loading ? 'fa-spinner fa-spin' : 'fa-sync'} mr-2`}></i>
                  Refresh
                </Button>
              </Col>
            </Row>

            {/* Boards Exam Date */}
            <Row className="mt-3">
              <Col lg="6" md="8">
                <div className="d-flex align-items-center bg-white rounded shadow-sm px-3 py-2">
                  <i className="fas fa-calendar-alt text-primary mr-2"></i>
                  <span className="font-weight-bold text-dark mr-3" style={{ whiteSpace: 'nowrap' }}>{getSpecialty(activeSpecialty).name} Boards Exam Date</span>
                  <Input
                    type="date"
                    bsSize="sm"
                    value={boardsExamDate}
                    onChange={(e) => setBoardsExamDate(e.target.value)}
                    style={{ maxWidth: '180px' }}
                    className="mr-2"
                  />
                  <Button
                    color="primary"
                    size="sm"
                    onClick={handleSaveBoardsDate}
                    disabled={boardsDateSaving || !boardsExamDate}
                    className="mr-1 w-100"
                  >
                    {boardsDateSaving ? <Spinner size="sm" /> : 'Save'}
                  </Button>
                  {boardsExamDate && (
                    <Button
                      color="danger"
                      size="sm"
                      outline
                      onClick={handleClearBoardsDate}
                      disabled={boardsDateSaving}
                    >
                      Clear
                    </Button>
                  )}
                </div>
              </Col>
            </Row>

            {/* Stats Cards */}
            <Row className="mt-4">
              <Col lg="3" md="6" className="mb-3">
                <Card className="card-stats shadow">
                  <CardBody>
                    <Row>
                      <div className="col">
                        <span className="h5 font-weight-bold text-uppercase text-muted mb-0">
                          Total Students
                        </span>
                        <span className="h2 font-weight-bold mb-0 d-block">
                          {loading ? <Spinner size="sm" /> : totalStudents}
                        </span>
                      </div>
                      <Col className="col-auto">
                        <div className="icon icon-shape bg-gradient-info text-white rounded-circle shadow">
                          <i className="fas fa-users" />
                        </div>
                      </Col>
                    </Row>
                  </CardBody>
                </Card>
              </Col>
              <Col lg="3" md="6" className="mb-3">
                <Card className="card-stats shadow">
                  <CardBody>
                    <Row>
                      <div className="col">
                        <span className="h5 font-weight-bold text-uppercase text-muted mb-0">
                          Completed Tests
                        </span>
                        <span className="h2 font-weight-bold mb-0 d-block">
                          {loading ? <Spinner size="sm" /> : overallStats.completedTests}
                        </span>
                      </div>
                      <Col className="col-auto">
                        <div className="icon icon-shape bg-gradient-success text-white rounded-circle shadow">
                          <i className="fas fa-check-circle" />
                        </div>
                      </Col>
                    </Row>
                  </CardBody>
                </Card>
              </Col>
              <Col lg="3" md="6" className="mb-3">
                <Card className="card-stats shadow">
                  <CardBody>
                    <Row>
                      <div className="col">
                        <span className="h5 font-weight-bold text-uppercase text-muted mb-0">
                          Avg Score
                        </span>
                        <span className="h2 font-weight-bold mb-0 d-block">
                          {loading ? <Spinner size="sm" /> : `${overallStats.averageScore}%`}
                        </span>
                      </div>
                      <Col className="col-auto">
                        <div className="icon icon-shape bg-gradient-warning text-white rounded-circle shadow">
                          <i className="fas fa-chart-line" />
                        </div>
                      </Col>
                    </Row>
                  </CardBody>
                </Card>
              </Col>
              <Col lg="3" md="6" className="mb-3">
                <Card className="card-stats shadow">
                  <CardBody>
                    <Row>
                      <div className="col">
                        <span className="h5 font-weight-bold text-uppercase text-muted mb-0">
                          Questions Answered
                        </span>
                        <span className="h2 font-weight-bold mb-0 d-block">
                          {loading ? <Spinner size="sm" /> : overallStats.totalQuestionsAnswered?.toLocaleString()}
                        </span>
                      </div>
                      <Col className="col-auto">
                        <div className="icon icon-shape bg-gradient-primary text-white rounded-circle shadow">
                          <i className="fas fa-question-circle" />
                        </div>
                      </Col>
                    </Row>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </div>
        </Container>
      </div>

      {/* Page content */}
      <Container className="mt--7 pb-5 mb-5" fluid>
        {/* Cache Rebuilding Banner */}
        {cacheRebuilding && (
          <Row className="mb-3">
            <Col>
              <div className="alert alert-info d-flex align-items-center mb-0 shadow-sm" role="alert">
                <i className="fas fa-sync fa-spin mr-2"></i>
                <span>Cache is rebuilding in the background. Data shown is computed live.</span>
              </div>
            </Col>
          </Row>
        )}

        {/* Error Alert */}
        {error && (
          <Row className="mb-4">
            <Col>
              <Card className="bg-danger shadow">
                <CardBody>
                  <div className="d-flex align-items-center text-white">
                    <i className="fas fa-exclamation-triangle fa-2x mr-3"></i>
                    <div>
                      <h4 className="mb-1 text-white">Error Loading Data</h4>
                      <p className="mb-0">{error}</p>
                      <small>Check the browser console for more details. You may need to create a Firestore composite index.</small>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        )}

        {/* Overall Answer Distribution */}
        <Row className="mb-4">
          <Col lg="4">
            <Card className="shadow">
              <CardHeader className="border-0">
                <h3 className="mb-0">Answer Distribution</h3>
              </CardHeader>
              <CardBody>
                {loading ? (
                  <div className="text-center py-4">
                    <Spinner />
                  </div>
                ) : (
                  <>
                    <div className="mb-3">
                      <div className="d-flex justify-content-between mb-1">
                        <span className="text-success font-weight-bold">Correct</span>
                        <span>{overallStats.totalCorrect?.toLocaleString()} ({overallStats.totalQuestionsAnswered > 0 ? Math.round((overallStats.totalCorrect / overallStats.totalQuestionsAnswered) * 100) : 0}%)</span>
                      </div>
                      <Progress
                        color="success"
                        value={overallStats.totalQuestionsAnswered > 0 ? (overallStats.totalCorrect / overallStats.totalQuestionsAnswered) * 100 : 0}
                      />
                    </div>
                    <div className="mb-3">
                      <div className="d-flex justify-content-between mb-1">
                        <span className="text-danger font-weight-bold">Incorrect</span>
                        <span>{overallStats.totalIncorrect?.toLocaleString()} ({overallStats.totalQuestionsAnswered > 0 ? Math.round((overallStats.totalIncorrect / overallStats.totalQuestionsAnswered) * 100) : 0}%)</span>
                      </div>
                      <Progress
                        color="danger"
                        value={overallStats.totalQuestionsAnswered > 0 ? (overallStats.totalIncorrect / overallStats.totalQuestionsAnswered) * 100 : 0}
                      />
                    </div>
                    <div className="mb-3">
                      <div className="d-flex justify-content-between mb-1">
                        <span className="text-warning font-weight-bold">Omitted</span>
                        <span>{overallStats.totalOmitted?.toLocaleString()} ({overallStats.totalQuestionsAnswered > 0 ? Math.round((overallStats.totalOmitted / overallStats.totalQuestionsAnswered) * 100) : 0}%)</span>
                      </div>
                      <Progress
                        color="warning"
                        value={overallStats.totalQuestionsAnswered > 0 ? (overallStats.totalOmitted / overallStats.totalQuestionsAnswered) * 100 : 0}
                      />
                    </div>
                    <hr />
                    <div className="d-flex justify-content-between">
                      <span className="text-muted">Avg Time/Question:</span>
                      <span className="font-weight-bold">{formatTime(overallStats.averageTimePerQuestion)}</span>
                    </div>
                    <div className="d-flex justify-content-between mt-2">
                      <span className="text-muted">Total Questions in Bank:</span>
                      <span className="font-weight-bold">{overallStats.totalQuestionsInBank?.toLocaleString()}</span>
                    </div>
                  </>
                )}
              </CardBody>
            </Card>
          </Col>
          <Col lg="8" className="mt-4 mt-lg-0">
            <Card className="shadow h-100">
              <CardHeader className="border-0">
                <h3 className="mb-0">Performance by Subject</h3>
              </CardHeader>
              <CardBody className="pt-0">
                {loading ? (
                  <div className="text-center py-4">
                    <Spinner />
                  </div>
                ) : subjectStats.length === 0 ? (
                  <p className="text-muted text-center py-4">No subject data available</p>
                ) : (
                  <div className="table-responsive" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                    <Table className="align-items-center table-flush mb-0">
                      <thead className="thead-light" style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                        <tr>
                          <th>Subject</th>
                          <th>Students</th>
                          <th>Questions</th>
                          <th>Correct %</th>
                          <th>Distribution</th>
                        </tr>
                      </thead>
                      <tbody>
                        {subjectStats.map((subject) => (
                          <tr key={subject.subjectId}>
                            <td className="font-weight-bold">{subject.subjectName}</td>
                            <td>{subject.studentsAttempted}</td>
                            <td>{subject.totalQuestions}</td>
                            <td>
                              <Badge color={getScoreColor(subject.percentCorrect)}>
                                {subject.percentCorrect}%
                              </Badge>
                            </td>
                            <td style={{ minWidth: '200px' }}>
                              <div className="d-flex align-items-center">
                                <div style={{ flex: 1 }}>
                                  <Progress multi>
                                    <Progress bar color="success" value={(subject.correct / subject.totalQuestions) * 100} />
                                    <Progress bar color="danger" value={(subject.incorrect / subject.totalQuestions) * 100} />
                                    <Progress bar color="warning" value={(subject.omitted / subject.totalQuestions) * 100} />
                                  </Progress>
                                </div>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Student Progress Card */}
        <Card className="shadow mb-4">
          <CardHeader className="border-0">
            <Row className="align-items-center">
              <Col xs="12" md="4">
                <h3 className="mb-0">
                  <i className="fas fa-users mr-2 text-primary"></i>
                  Student Progress
                </h3>
              </Col>
              <Col xs="12" md="8" className="mt-2 mt-md-0">
                <div className="d-flex flex-column flex-md-row justify-content-md-end align-items-stretch align-items-md-center">
                  {/* Activity Filter */}
                  <InputGroup style={{ maxWidth: '200px' }} className="mb-2 mb-md-0 mr-md-2">
                    <InputGroupAddon addonType="prepend">
                      <InputGroupText>
                        <i className="fas fa-filter" />
                      </InputGroupText>
                    </InputGroupAddon>
                    <Input
                      type="select"
                      value={activityFilter}
                      onChange={(e) => setActivityFilter(e.target.value)}
                    >
                      <option value="all">All Students ({students.length})</option>
                      <option value="completed">Completed Tests ({students.filter(s => s.completedTests > 0).length})</option>
                      <option value="in_progress">In Progress Only ({students.filter(s => s.inProgressTests > 0 && s.completedTests === 0).length})</option>
                      <option value="no_activity">No Activity ({students.filter(s => s.totalTests === 0).length})</option>
                    </Input>
                  </InputGroup>
                  {/* Search */}
                  <InputGroup style={{ maxWidth: '250px' }}>
                    <InputGroupAddon addonType="prepend">
                      <InputGroupText>
                        <i className="fas fa-search" />
                      </InputGroupText>
                    </InputGroupAddon>
                    <Input
                      placeholder="Search students..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </InputGroup>
                </div>
              </Col>
            </Row>
          </CardHeader>
          <CardBody>
            {loading ? (
              <div className="text-center py-5">
                <Spinner />
                <p className="mt-3 text-muted">Loading student data...</p>
              </div>
            ) : filteredStudents.length === 0 ? (
              <p className="text-muted text-center py-5">No students found</p>
            ) : (
              <Table responsive className="align-items-center table-flush">
                <thead className="thead-light">
                  <tr>
                    <th style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }} onClick={() => handleSort('name')}>Student{renderSortIcon('name')}</th>
                    <th style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }} onClick={() => handleSort('pmrProgram')}>Residency Program{renderSortIcon('pmrProgram')}</th>
                    <th style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }} onClick={() => handleSort('year')}>Year{renderSortIcon('year')}</th>
                    <th style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }} onClick={() => handleSort('createdAt')}>Date Joined{renderSortIcon('createdAt')}</th>
                    <th style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }} onClick={() => handleSort('access')}>Access{renderSortIcon('access')}</th>
                    <th style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }} onClick={() => handleSort('completedTests')}>Tests{renderSortIcon('completedTests')}</th>
                    <th style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }} onClick={() => handleSort('totalQuestions')}>Questions{renderSortIcon('totalQuestions')}</th>
                    <th style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }} onClick={() => handleSort('percentComplete')}>% Complete{renderSortIcon('percentComplete')}</th>
                    <th style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }} onClick={() => handleSort('percentCorrect')}>% Correct{renderSortIcon('percentCorrect')}</th>
                    <th style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }} onClick={() => handleSort('avgTimePerQuestion')}>Avg Time{renderSortIcon('avgTimePerQuestion')}</th>
                    <th style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }} onClick={() => handleSort('lastActivityDate')}>Last Active{renderSortIcon('lastActivityDate')}</th>
                    <th style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }} onClick={() => handleSort('heardAboutUs')}>Heard About Us{renderSortIcon('heardAboutUs')}</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedStudents.map((student) => (
                    <tr key={student.userHandle}>
                      <td>
                        <div className="d-flex align-items-center">
                          {student.profilePicture ? (
                            <img
                              src={student.profilePicture}
                              alt=""
                              className="rounded-circle mr-2"
                              style={{ width: '32px', height: '32px', objectFit: 'cover' }}
                            />
                          ) : (
                            <div
                              className="rounded-circle bg-gradient-primary text-white d-flex align-items-center justify-content-center mr-2"
                              style={{ width: '32px', height: '32px', fontSize: '14px' }}
                            >
                              {student.name?.charAt(0)?.toUpperCase() || '?'}
                            </div>
                          )}
                          <div>
                            <span className="font-weight-bold">{student.name}</span>
                            <small className="d-block text-muted">{student.email}</small>
                          </div>
                        </div>
                      </td>
                      <td style={{ maxWidth: '240px', whiteSpace: 'normal' }}>
                        {student[fields.pmrProgram] ? (
                          <small>{student[fields.pmrProgram]}</small>
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>
                      <td>
                        {student[fields.year] ? (
                          <small>{student[fields.year]}</small>
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        {student.createdAt ? (
                          <small>{formatDate(student.createdAt)}</small>
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>
                      <td>
                        {student[fields.lifetimeAccess] ? (
                          <Badge color="primary" pill>
                            <i className="fas fa-infinity mr-1"></i>Lifetime
                          </Badge>
                        ) : student[fields.ultimateAdvisingStudent] ? (
                          <Badge color="info" pill>
                            <i className="fas fa-star mr-1"></i>UAS
                          </Badge>
                        ) : student[fields.planId] ? (
                          <Badge color="success" pill>
                            <i className="fas fa-crown mr-1"></i>
                            {(() => {
                              const plan = getPlanById(student[fields.planId]);
                              if (plan) return plan.months + ' Mo';
                              return student[fields.planId];
                            })()}
                          </Badge>
                        ) : student[fields.trialStartDate] ? (
                          <Badge color="warning" pill>
                            <i className="fas fa-hourglass-half mr-1"></i>Trial
                          </Badge>
                        ) : (
                          <Badge color="light" pill>
                            <i className="fas fa-user mr-1"></i>Free
                          </Badge>
                        )}
                      </td>
                      <td>
                        {student.totalTests > 0 ? (
                          <span>
                            {student.completedTests}
                            {student.inProgressTests > 0 && (
                              <small className="text-warning ml-1">({student.inProgressTests} in progress)</small>
                            )}
                          </span>
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>
                      <td>{student.totalQuestions > 0 ? student.totalQuestions : <span className="text-muted">-</span>}</td>
                      <td>
                        {student.totalQuestions > 0 ? (
                          <div className="d-flex align-items-center">
                            <span className="mr-2">{student.percentComplete}%</span>
                            <Progress
                              style={{ width: '60px', height: '6px' }}
                              color={getProgressColor(student.percentComplete)}
                              value={student.percentComplete}
                            />
                          </div>
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>
                      <td>
                        {student.totalQuestions > 0 ? (
                          <Badge color={getScoreColor(student.percentCorrect)}>
                            {student.percentCorrect}%
                          </Badge>
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>
                      <td>{student.totalQuestions > 0 ? formatTime(student.avgTimePerQuestion) : <span className="text-muted">-</span>}</td>
                      <td>{student.lastActivityDate ? formatDate(student.lastActivityDate) : <span className="text-muted">Never</span>}</td>
                      <td>
                        {student.heardAboutUs ? (
                          <small>{student.heardAboutUs}</small>
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>
                      <td>
                        <Button
                          color="primary"
                          size="sm"
                          onClick={() => handleViewStudent(student.userHandle)}
                          disabled={student.totalTests === 0}
                          title={student.totalTests === 0 ? 'No test data to view' : 'View details'}
                        >
                          <i className="fas fa-eye"></i>
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </CardBody>
          <CardFooter className="border-0">
            <div className="d-flex flex-column flex-sm-row justify-content-between align-items-center">
              <div className="text-muted small mb-2 mb-sm-0">
                {sortedStudents.length > 0
                  ? `Showing ${studentStartIndex + 1} to ${Math.min(studentStartIndex + studentPerPage, sortedStudents.length)} of ${sortedStudents.length} students`
                  : '0 students'}
              </div>
              <div className="d-flex align-items-center">
                <Button
                  color="primary"
                  size="sm"
                  onClick={() => setStudentPage(prev => Math.max(1, prev - 1))}
                  disabled={studentPage === 1 || studentTotalPages <= 1}
                  className="mr-2"
                >
                  <i className="fas fa-chevron-left"></i>
                  <span className="d-none d-sm-inline ml-1">Previous</span>
                </Button>
                <span className="mx-2 text-nowrap">
                  Page {studentPage} of {studentTotalPages || 1}
                </span>
                <Button
                  color="primary"
                  size="sm"
                  onClick={() => setStudentPage(prev => Math.min(studentTotalPages, prev + 1))}
                  disabled={studentPage === studentTotalPages || studentTotalPages <= 1}
                  className="ml-2"
                >
                  <span className="d-none d-sm-inline mr-1">Next</span>
                  <i className="fas fa-chevron-right"></i>
                </Button>
              </div>
            </div>
          </CardFooter>
        </Card>

        {/* Question Analytics Card */}
        <Card className="shadow">
          <CardHeader className="border-0">
            <Row className="align-items-center">
              <Col>
                <h3 className="mb-0">
                  <i className="fas fa-chart-bar mr-2 text-info"></i>
                  Question Analytics
                </h3>
              </Col>
              <Col className="text-right">
                <InputGroup style={{ maxWidth: '200px', marginLeft: 'auto' }}>
                  <InputGroupAddon addonType="prepend">
                    <InputGroupText>
                      <i className="fas fa-filter" />
                    </InputGroupText>
                  </InputGroupAddon>
                  <Input
                    type="select"
                    value={subjectFilter}
                    onChange={(e) => setSubjectFilter(e.target.value)}
                  >
                    <option value="all">All Subjects</option>
                    {uniqueSubjects.map(subject => (
                      <option key={subject.id} value={subject.id}>
                        {subject.name}
                      </option>
                    ))}
                  </Input>
                </InputGroup>
              </Col>
            </Row>
          </CardHeader>
          <CardBody>
            {questionLoading ? (
              <div className="text-center py-5">
                <Spinner />
                <p className="mt-3 text-muted">Loading question analytics...</p>
              </div>
            ) : filteredQuestions.length === 0 ? (
              <p className="text-muted text-center py-5">No questions found</p>
            ) : (
              <Table responsive className="align-items-center table-flush">
                <thead className="thead-light">
                  <tr>
                    <th style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }} onClick={() => handleQuestionSort('questionCode')}>Question{renderQSortIcon('questionCode')}</th>
                    <th style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }} onClick={() => handleQuestionSort('subjectName')}>Subject{renderQSortIcon('subjectName')}</th>
                    <th style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }} onClick={() => handleQuestionSort('unitName')}>Unit{renderQSortIcon('unitName')}</th>
                    <th style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }} onClick={() => handleQuestionSort('topicName')}>Topic{renderQSortIcon('topicName')}</th>
                    <th style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }} onClick={() => handleQuestionSort('timesAnswered')}>Answered{renderQSortIcon('timesAnswered')}</th>
                    <th style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }} onClick={() => handleQuestionSort('dist_0')}>A{renderQSortIcon('dist_0')}</th>
                    <th style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }} onClick={() => handleQuestionSort('dist_1')}>B{renderQSortIcon('dist_1')}</th>
                    <th style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }} onClick={() => handleQuestionSort('dist_2')}>C{renderQSortIcon('dist_2')}</th>
                    <th style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }} onClick={() => handleQuestionSort('dist_3')}>D{renderQSortIcon('dist_3')}</th>
                    <th style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }} onClick={() => handleQuestionSort('avgTimeSpent')}>Avg Time{renderQSortIcon('avgTimeSpent')}</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedQuestions.map((question) => (
                    <tr key={question.questionId}>
                      <td>
                        <span className="font-weight-bold">{question.questionCode}</span>
                      </td>
                      <td>
                        <Badge color="info">{question.subjectName}</Badge>
                      </td>
                      <td>
                        <Badge color="primary">{question.unitName}</Badge>
                      </td>
                      <td>
                        {question.topicName && question.topicName !== '-' ? (
                          <Badge color="warning">{question.topicName}</Badge>
                        ) : (
                          <small className="text-muted">-</small>
                        )}
                      </td>
                      <td>{question.timesAnswered}</td>
                      {/* Choice A */}
                      <td>
                        {question.answerDistribution?.[0] ? (
                          <Badge
                            color={
                              question.answerDistribution[0].percentage === 0 ? 'light' :
                              question.answerDistribution[0].isCorrect ? 'success' :
                              question.answerDistribution[0].percentage > 30 ? 'danger' : 'secondary'
                            }
                            style={{ minWidth: '40px' }}
                          >
                            {question.answerDistribution[0].percentage}%
                          </Badge>
                        ) : <small className="text-muted">-</small>}
                      </td>
                      {/* Choice B */}
                      <td>
                        {question.answerDistribution?.[1] ? (
                          <Badge
                            color={
                              question.answerDistribution[1].percentage === 0 ? 'light' :
                              question.answerDistribution[1].isCorrect ? 'success' :
                              question.answerDistribution[1].percentage > 30 ? 'danger' : 'secondary'
                            }
                            style={{ minWidth: '40px' }}
                          >
                            {question.answerDistribution[1].percentage}%
                          </Badge>
                        ) : <small className="text-muted">-</small>}
                      </td>
                      {/* Choice C */}
                      <td>
                        {question.answerDistribution?.[2] ? (
                          <Badge
                            color={
                              question.answerDistribution[2].percentage === 0 ? 'light' :
                              question.answerDistribution[2].isCorrect ? 'success' :
                              question.answerDistribution[2].percentage > 30 ? 'danger' : 'secondary'
                            }
                            style={{ minWidth: '40px' }}
                          >
                            {question.answerDistribution[2].percentage}%
                          </Badge>
                        ) : <small className="text-muted">-</small>}
                      </td>
                      {/* Choice D */}
                      <td>
                        {question.answerDistribution?.[3] ? (
                          <Badge
                            color={
                              question.answerDistribution[3].percentage === 0 ? 'light' :
                              question.answerDistribution[3].isCorrect ? 'success' :
                              question.answerDistribution[3].percentage > 30 ? 'danger' : 'secondary'
                            }
                            style={{ minWidth: '40px' }}
                          >
                            {question.answerDistribution[3].percentage}%
                          </Badge>
                        ) : <small className="text-muted">-</small>}
                      </td>
                      <td>{formatTime(question.avgTimeSpent)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </CardBody>
          <CardFooter className="border-0">
            <div className="d-flex flex-column flex-sm-row justify-content-between align-items-center">
              <div className="text-muted small mb-2 mb-sm-0">
                {sortedQuestions.length > 0
                  ? `Showing ${questionStartIndex + 1} to ${Math.min(questionStartIndex + questionPerPage, sortedQuestions.length)} of ${sortedQuestions.length} questions`
                  : '0 questions'}
              </div>
              <div className="d-flex align-items-center">
                <Button
                  color="primary"
                  size="sm"
                  onClick={() => setQuestionPage(prev => Math.max(1, prev - 1))}
                  disabled={questionPage === 1 || questionTotalPages <= 1}
                  className="mr-2"
                >
                  <i className="fas fa-chevron-left"></i>
                  <span className="d-none d-sm-inline ml-1">Previous</span>
                </Button>
                <span className="mx-2 text-nowrap">
                  Page {questionPage} of {questionTotalPages || 1}
                </span>
                <Button
                  color="primary"
                  size="sm"
                  onClick={() => setQuestionPage(prev => Math.min(questionTotalPages, prev + 1))}
                  disabled={questionPage === questionTotalPages || questionTotalPages <= 1}
                  className="ml-2"
                >
                  <span className="d-none d-sm-inline mr-1">Next</span>
                  <i className="fas fa-chevron-right"></i>
                </Button>
              </div>
            </div>
          </CardFooter>
        </Card>

        {/* Student Details Modal */}
        <Modal isOpen={studentModalOpen} toggle={() => setStudentModalOpen(false)} size="lg">
          <ModalHeader toggle={() => setStudentModalOpen(false)}>
            {selectedStudent?.name || 'Student Details'}
          </ModalHeader>
          <ModalBody>
            {studentLoading ? (
              <div className="text-center py-5">
                <Spinner />
              </div>
            ) : selectedStudent ? (
              <>
                {/* Student Overview */}
                <Row className="mb-4">
                  <Col md="6">
                    <Card className="bg-gradient-default shadow-sm">
                      <CardBody>
                        <div className="d-flex align-items-center">
                          {selectedStudent.profilePicture ? (
                            <img
                              src={selectedStudent.profilePicture}
                              alt=""
                              className="rounded-circle mr-3"
                              style={{ width: '64px', height: '64px', objectFit: 'cover' }}
                            />
                          ) : (
                            <div
                              className="rounded-circle bg-white text-primary d-flex align-items-center justify-content-center mr-3"
                              style={{ width: '64px', height: '64px', fontSize: '24px', fontWeight: 'bold' }}
                            >
                              {selectedStudent.name?.charAt(0)?.toUpperCase() || '?'}
                            </div>
                          )}
                          <div>
                            <h4 className="text-white mb-0">{selectedStudent.name}</h4>
                            <p className="text-white-50 mb-0">{selectedStudent.email}</p>
                          </div>
                        </div>
                      </CardBody>
                    </Card>
                  </Col>
                  <Col md="6">
                    <Row>
                      <Col xs="6" className="mb-2">
                        <Card className="shadow-sm">
                          <CardBody className="py-2 text-center">
                            <small className="text-muted d-block">Tests</small>
                            <h4 className="mb-0">{selectedStudent.completedTests}</h4>
                          </CardBody>
                        </Card>
                      </Col>
                      <Col xs="6" className="mb-2">
                        <Card className="shadow-sm">
                          <CardBody className="py-2 text-center">
                            <small className="text-muted d-block">Questions</small>
                            <h4 className="mb-0">{selectedStudent.totalQuestions}</h4>
                          </CardBody>
                        </Card>
                      </Col>
                      <Col xs="6">
                        <Card className="shadow-sm">
                          <CardBody className="py-2 text-center">
                            <small className="text-muted d-block">Correct %</small>
                            <h4 className={`mb-0 text-${getScoreColor(selectedStudent.percentCorrect)}`}>
                              {selectedStudent.percentCorrect}%
                            </h4>
                          </CardBody>
                        </Card>
                      </Col>
                      <Col xs="6">
                        <Card className="shadow-sm">
                          <CardBody className="py-2 text-center">
                            <small className="text-muted d-block">Unique Q's</small>
                            <h4 className="mb-0">{selectedStudent.uniqueQuestionsAttempted}</h4>
                          </CardBody>
                        </Card>
                      </Col>
                    </Row>
                  </Col>
                </Row>

                {/* Answer Breakdown */}
                <h5 className="mb-3">Answer Breakdown</h5>
                <Row className="mb-4">
                  <Col md="4" className="text-center">
                    <h3 className="text-success mb-0">{selectedStudent.totalCorrect}</h3>
                    <small className="text-muted">Correct</small>
                  </Col>
                  <Col md="4" className="text-center">
                    <h3 className="text-danger mb-0">{selectedStudent.totalIncorrect}</h3>
                    <small className="text-muted">Incorrect</small>
                  </Col>
                  <Col md="4" className="text-center">
                    <h3 className="text-warning mb-0">{selectedStudent.totalOmitted}</h3>
                    <small className="text-muted">Omitted</small>
                  </Col>
                </Row>

                {/* Subject Performance */}
                {selectedStudent.subjectStats?.length > 0 && (
                  <>
                    <h5 className="mb-3">Performance by Subject</h5>
                    <Table responsive size="sm" className="mb-4">
                      <thead className="thead-light">
                        <tr>
                          <th>Subject</th>
                          <th>Questions</th>
                          <th>Correct</th>
                          <th>% Correct</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedStudent.subjectStats.map((subject) => (
                          <tr key={subject.subjectId}>
                            <td>{subject.subjectName}</td>
                            <td>{subject.totalQuestions}</td>
                            <td>{subject.correct}</td>
                            <td>
                              <Badge color={getScoreColor(subject.percentCorrect)}>
                                {subject.percentCorrect}%
                              </Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </>
                )}

                {/* Recent Tests */}
                {selectedStudent.tests?.length > 0 && (
                  <>
                    <h5 className="mb-3">Recent Tests</h5>
                    <Table responsive size="sm">
                      <thead className="thead-light">
                        <tr>
                          <th>Test Name</th>
                          <th>Status</th>
                          <th>Score</th>
                          <th>Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedStudent.tests.slice(0, 10).map((test) => (
                          <tr key={test.testId}>
                            <td>{test.testName}</td>
                            <td>
                              <Badge color={test.status === 'completed' ? 'success' : 'warning'}>
                                {test.status}
                              </Badge>
                            </td>
                            <td>
                              {test.score !== undefined ? (
                                <Badge color={getScoreColor(test.score)}>
                                  {test.score}%
                                </Badge>
                              ) : '-'}
                            </td>
                            <td>{formatDate(test.completedAt || test.createdAt)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </>
                )}
              </>
            ) : (
              <p className="text-muted text-center">No data available</p>
            )}
          </ModalBody>
        </Modal>
      </Container>
    </>
  );
};

const mapStateToProps = (state) => ({
  adminAnalytics: state.adminAnalytics
});

export default connect(mapStateToProps)(DashboardPage);
